// src/features/vita/chat/vitaPrefs.js
//
// Preferências de RENDERIZAÇÃO do chat (fonte, tamanho e tema da conversa).
//
// Ficam em localStorage, não no servidor, de propósito: são ajustes do
// APARELHO. Quem aumenta a fonte no celular pequeno não quer a fonte grande no
// tablet, e uma preferência de leitura não deveria depender de rede para valer.
//
// O que É do servidor fica no CHAT_META (notificações, horário de silêncio e
// idioma) — o push é montado no backend e precisa desses três.

const CHAVE = "vitaChat:prefs";

// Famílias por STACK do sistema: nenhuma webfont nova é baixada, então trocar a
// fonte é instantâneo e não custa um request no meio da conversa.
export const FONTES = [
  { id: "padrao", label: "Padrão do app", stack: "inherit" },
  {
    id: "sistema",
    label: "Sistema",
    stack:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  { id: "serifada", label: "Serifada", stack: 'Georgia, "Times New Roman", serif' },
  {
    id: "arredondada",
    label: "Arredondada",
    stack: '"SF Pro Rounded", "Nunito", "Segoe UI", system-ui, sans-serif',
  },
  { id: "mono", label: "Monoespaçada", stack: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' },
];

export const TAMANHOS = [
  { id: "pequena", label: "Pequena", px: 13 },
  { id: "media", label: "Média", px: 14.5 },
  { id: "grande", label: "Grande", px: 16.5 },
  { id: "maior", label: "Maior", px: 18.5 },
];

// TEMAS DA CONVERSA
// ─────────────────
// Hoje existe UM tema: o escuro com o padrão de rabiscos, que é o fundo
// desenhado em CSS (ver `lista-base` no VitaChat.module.scss).
//
// Espaço reservado para temas com IMAGEM (webp). Quando os arquivos entrarem em
// `public/assets/vita/temas/`, basta descomentar as linhas abaixo e o seletor
// já renderiza os novos cartões — nenhuma outra mudança é necessária, porque
// `aplicarPrefs` só escreve a var `--vita-fundo-conversa`.
//
// export const TEMA_IMAGEM_BASE = `${process.env.PUBLIC_URL || ""}/assets/vita/temas`;
//
export const TEMAS = [
  {
    id: "fitness",
    label: "Fitness",
    // null = usa o padrão de rabiscos em CSS (nenhuma imagem é baixada).
    imagem: null,
  },
  // { id: "academia", label: "Academia", imagem: `${TEMA_IMAGEM_BASE}/academia.webp` },
  // { id: "cozinha",  label: "Cozinha",  imagem: `${TEMA_IMAGEM_BASE}/cozinha.webp`  },
  // { id: "liso",     label: "Liso",     imagem: null                                 },
];

export const PREFS_PADRAO = {
  fonte: "padrao",
  tamanho: "media",
  tema: "fitness",
};

export function lerPrefs() {
  try {
    const cru = localStorage.getItem(CHAVE);
    return cru ? { ...PREFS_PADRAO, ...JSON.parse(cru) } : { ...PREFS_PADRAO };
  } catch {
    return { ...PREFS_PADRAO };
  }
}

export function gravarPrefs(prefs) {
  try {
    localStorage.setItem(CHAVE, JSON.stringify({ ...PREFS_PADRAO, ...prefs }));
  } catch {
    /* quota cheia — a preferência vale só nesta sessão */
  }
}

export function fontePorId(id) {
  return FONTES.find((f) => f.id === id) || FONTES[0];
}

export function tamanhoPorId(id) {
  return TAMANHOS.find((t) => t.id === id) || TAMANHOS[1];
}

export function temaPorId(id) {
  return TEMAS.find((t) => t.id === id) || TEMAS[0];
}

/**
 * Traduz as preferências em CSS custom properties.
 *
 * Devolve um objeto de estilo em vez de mexer no DOM: aplicado no container do
 * chat, o escopo é o próprio componente — nada vaza para o resto do app, e o
 * React continua dono da árvore.
 */
export function estiloDasPrefs(prefs) {
  const fonte = fontePorId(prefs.fonte);
  const tamanho = tamanhoPorId(prefs.tamanho);
  const tema = temaPorId(prefs.tema);
  const estilo = {
    "--vita-fonte": fonte.stack,
    "--vita-fonte-tamanho": `${tamanho.px}px`,
  };
  // Tema com imagem sobrescreve o padrão de rabiscos; sem imagem, a var fica
  // indefinida e o CSS cai no desenho original.
  if (tema.imagem) estilo["--vita-fundo-conversa"] = `url("${tema.imagem}")`;
  return estilo;
}

export const IDIOMAS = [
  { id: "pt-BR", label: "Português (Brasil)" },
  { id: "es", label: "Espanhol" },
  { id: "en", label: "Inglês" },
  { id: "fr", label: "Francês" },
];

export function idiomaLabel(id) {
  return (IDIOMAS.find((i) => i.id === id) || IDIOMAS[0]).label;
}
